export type MarketingFaqItem = {
  question: string;
  answer: string;
};

export type MarketingNavItem = {
  title: string;
  href: string;
};

export type HeroStat = {
  value: string;
  label: string;
};

export type FeatureItem = {
  title: string;
  description: string;
  icon: string;
};

export type UseCaseItem = {
  title: string;
  audience: string;
  summary: string;
  example: string;
  outcomes: string[];
  icon: string;
};

export type WorkflowStep = {
  step: string;
  title: string;
  description: string;
  icon: string;
};

export type DownloadLink = {
  title: string;
  href: string;
  description: string;
  badge: string;
  external?: boolean;
};

export type AboutSection = {
  title: string;
  body: string[];
};

export const externalLinks = {
  github: "https://github.com/netease-youdao/LobsterAI",
  official: "https://lobsterai.youdao.com/",
};

export const marketingNavigation: MarketingNavItem[] = [
  { title: "功能", href: "/features" },
  { title: "场景", href: "/use-cases" },
  { title: "下载", href: "/download" },
  { title: "关于", href: "/about" },
];

export const marketingFooterColumns = [
  {
    title: "站点",
    items: [
      { title: "首页", href: "/" },
      { title: "功能", href: "/features" },
      { title: "场景", href: "/use-cases" },
      { title: "下载", href: "/download" },
    ],
  },
  {
    title: "资源",
    items: [
      { title: "GitHub 仓库", href: externalLinks.github, external: true },
      { title: "官方入口", href: externalLinks.official, external: true },
      { title: "关于", href: "/about" },
    ],
  },
  {
    title: "法务",
    items: [
      { title: "隐私政策", href: "/privacy-policy" },
      { title: "使用条款", href: "/terms-of-service" },
      { title: "退款说明", href: "/refund-policy" },
    ],
  },
];

export const homeHeroStats: HeroStat[] = [
  { value: "7×24", label: "持续执行的 Agent 工作节奏" },
  { value: "多工具", label: "网页、文件、命令与 IM 协同" },
  { value: "Windows / macOS", label: "桌面办公与本地任务兼容" },
];

export const coreFeatures: FeatureItem[] = [
  {
    title: "全场景个人助理 Agent",
    description:
      "LobsterAI 不是单一问答助手，而是围绕真实工作流持续接活、拆解、执行和回收结果的个人 Agent。",
    icon: "bot",
  },
  {
    title: "本地与沙箱双执行面",
    description:
      "既能处理本地文件、命令和应用，也能在受控环境中完成联网检索、资料整理和自动化动作。",
    icon: "server",
  },
  {
    title: "多工具联动",
    description:
      "网页搜索、文档生成、表格整理、命令执行、截图回传和消息通知可以在同一条任务链里衔接。",
    icon: "wrench",
  },
  {
    title: "办公内容生产",
    description:
      "覆盖写邮件、写纪要、做汇报、做表格、整理资料、生成行动项等高频办公室任务。",
    icon: "message-square",
  },
  {
    title: "研究与分析",
    description:
      "把公开网页、知识文档和用户输入串起来，快速形成分析结论、对比视图和可执行摘要。",
    icon: "chart-column",
  },
  {
    title: "远程协作入口",
    description:
      "支持通过 IM 或外部指令触发任务，让 LobsterAI 在你不盯着屏幕时继续推进工作。",
    icon: "globe",
  },
  {
    title: "可持续上下文",
    description:
      "围绕长期任务保留上下文、偏好和近期工作状态，让后续协作不是每次都从空白开始。",
    icon: "layers",
  },
  {
    title: "权限与边界清晰",
    description:
      "把工具能力、可见数据和执行边界显式化，避免自动化任务变成不可审计的黑盒。",
    icon: "shield",
  },
];

export const useCases: UseCaseItem[] = [
  {
    title: "办公写作与汇报",
    audience: "面向日常办公人群",
    summary:
      "让 Agent 把会议纪要、周报、方案摘要、汇报材料和待办拆解串起来，而不是只给一段零散回答。",
    example: "整理会议录音摘要，输出周报初稿，并生成需要跟进的负责人列表。",
    outcomes: ["更少重复整理", "更快产出首稿", "更清晰的行动项"],
    icon: "message-circle-more",
  },
  {
    title: "搜索研究与信息归纳",
    audience: "面向研究和信息工作者",
    summary:
      "把网页搜索、信息筛选、引用归纳和结论输出连成一个持续流程，减少手工切换标签页的时间。",
    example: "对一个行业主题做公开资料收集，汇总成结构化洞察，并标记可继续跟进的来源。",
    outcomes: ["检索更系统", "总结更结构化", "可复用的研究脉络"],
    icon: "globe",
  },
  {
    title: "数据整理与轻分析",
    audience: "面向运营与分析岗位",
    summary:
      "将表格、文档和网页数据整理成可阅读的结论，适合日报、周报、对比分析和轻量决策支持。",
    example: "读取多个数据来源，统一格式后生成异常点摘要和给管理层看的结论版。",
    outcomes: ["减少机械汇总", "加快分析首轮", "结论表达更直接"],
    icon: "chart-column",
  },
  {
    title: "个人助理与自动化执行",
    audience: "面向高频多任务用户",
    summary:
      "把提醒、整理、收集、发送和后续追踪交给 Agent，让个人任务管理更接近持续协作而不是一次性命令。",
    example: "每天固定时间抓取关注主题，整理成摘要并推送到你的沟通渠道。",
    outcomes: ["减少碎片事务", "更稳的执行节奏", "长期任务不断线"],
    icon: "zap",
  },
];

export const workflowSteps: WorkflowStep[] = [
  {
    step: "01",
    title: "描述目标",
    description: "把你要完成的任务、边界和预期结果交给 LobsterAI，它负责拆出可执行步骤。",
    icon: "sparkles",
  },
  {
    step: "02",
    title: "调用工具执行",
    description: "Agent 在网页、文件、命令、资料与消息入口之间穿梭，把任务真正推进而不是停在建议层。",
    icon: "wrench",
  },
  {
    step: "03",
    title: "输出结果并继续协作",
    description: "结果会回收到文档、摘要、表格、消息或后续待办中，让下一轮协作直接接上上下文。",
    icon: "rocket",
  },
];

export const faqItems: MarketingFaqItem[] = [
  {
    question: "LobsterAI 是什么？",
    answer:
      "LobsterAI 是一个面向真实工作任务的 AI Agent 产品，目标不是只回答问题，而是把信息检索、内容生产、工具执行和后续协作串成可持续流程。",
  },
  {
    question: "这个站点和 LobsterAI 官方是什么关系？",
    answer:
      "这个站点使用 LobsterAI 品牌做产品式介绍，并把主要下载入口指向公开的 GitHub 仓库与官方入口，帮助用户快速理解产品能力与使用方向。",
  },
  {
    question: "它适合哪些场景？",
    answer:
      "目前最适合办公写作、资料研究、信息归纳、数据整理、日常自动化和持续性个人助理任务。",
  },
  {
    question: "主 CTA 为什么跳 GitHub？",
    answer:
      "当前任务 1 的目标是先把官网与关键词站搭起来，因此默认把最强转化动作指向公开仓库，用户可以直接查看说明、下载或继续了解。",
  },
  {
    question: "这个站点会提供付费购买吗？",
    answer:
      "当前版本不提供站内付费购买，重点是品牌展示、能力介绍、SEO 承接与外部入口导流。",
  },
];

export const downloadLinks: DownloadLink[] = [
  {
    title: "前往 GitHub 仓库",
    href: externalLinks.github,
    description: "查看 README、安装说明、版本信息和最新提交，是当前默认主下载入口。",
    badge: "主入口",
    external: true,
  },
  {
    title: "访问官方页面",
    href: externalLinks.official,
    description: "查看有道公开产品信息和官方产品表达，用于交叉理解 LobsterAI 的定位。",
    badge: "官方信息",
    external: true,
  },
];

export const downloadChecklist = [
  "先确认你要解决的是持续任务，而不是单轮对话。",
  "优先从 GitHub 阅读安装方式、环境要求和示例。",
  "在本地或测试环境先跑通基础流程，再扩展到长期使用场景。",
  "把高价值任务优先落在写作、研究、整理和自动化这几类高频动作上。",
];

export const aboutSections: AboutSection[] = [
  {
    title: "产品定位",
    body: [
      "LobsterAI 更像一个持续工作的 Agent，而不是只回答问题的聊天框。它强调任务推进、工具调用、结果回收和下一步协作。",
      "如果说普通助手擅长给建议，LobsterAI 的重点则是把建议落成可见动作和可交付结果。",
    ],
  },
  {
    title: "为什么它有关键词站价值",
    body: [
      "围绕 LobsterAI 的搜索意图天然集中在“是什么、怎么用、适合谁、怎么下载、和普通 AI 助手有什么不同”这几个问题。",
      "因此官网型关键词站最适合先承接认知型和下载型流量，再逐步扩展到教程与场景内容。",
    ],
  },
  {
    title: "它和普通 AI 聊天工具的差别",
    body: [
      "LobsterAI 更强调持续上下文、多工具协同、本地环境参与和任务闭环，而不仅仅是一次性的文本生成。",
      "这意味着它更适合真正要被推进的工作，而不是只停留在灵感和建议层。",
    ],
  },
  {
    title: "当前站点版本边界",
    body: [
      "当前版本优先完成品牌官网、能力说明、下载导流与基础 SEO 面，不在站内提供定价和支付流程。",
      "后续如果继续做任务 4、任务 5、任务 6，再补上线、统计、性能和视觉强化会更自然。",
    ],
  },
];
